import React, { Component } from 'react';
import PropTypes from 'prop-types';
import CardLabel from 'components/connectedForm/CardLabel';
import CardNumber from '../../components/connectedForm/CardNumber';
import styled, { css } from 'styled-components';
import { TooltipHost } from '@fluentui/react/lib/Tooltip';
import { p4Tokens } from '../../styles/themes/default/p4FluentTheme';
import { Icon } from '@fluentui/react/lib/Icon';

const CardContainer = styled.div`
  flex-grow: 1;
  flex-basis: auto;
  width: auto;
  max-width: none;
  text-align: left;
  position: relative;
`;

const ActionIcon = styled(Icon)`
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  cursor: pointer;
  margin-right: 2px;
  color: ${(props) => props.theme.colors.inputControl};
  &:hover {
    color: ${(props) => props.theme.colors.primary};
  }
  font-weight: bold;
`;

export const tagColor = (props) => {
  const tag = props.value > 0;
  return css`
    border-right: 3px solid ${(props) => (tag ? p4Tokens.statusFlagSuccess : p4Tokens.statusFlagWarning)};
  `;
};

const CardBlock = styled.div`
  box-sizing: border-box;
  flex: 0 1 auto;
  flex-direction: row;
  flex-wrap: wrap;
  padding-right: 2px;
  padding-left: 5px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans',
    'Droid Sans', 'Helvetica Neue', sans-serif;
  margin-bottom: ${(props) => (props.collapsed ? '0px' : '20px')};
  ${(props) => (props.showTag ? tagColor(props) : 'border: 3px solid transparent')};
`;

class ValueCard extends Component {
  focus() {
    this.node.focus();
  }

  render() {
    const {
      id,
      label,
      labelPosition,
      includeLabel,
      flexBox,
      showTag,
      value,
      collapsed,
      align,
      onClick,
      color,
      secondaryLabel, //Secondary label for extra information, e.g. price break quantity data.
      secondaryLabelFontStyle,
      onActionIconClick,
      actionIconName,
      actionIconHint,
    } = this.props;

    const labelProps = {
      flexBox,
      id,
      includeLabel,
      label,
      align,
      clickable: !!onClick,
      color,
    };

    return (
      <TooltipHost content={this.props.tooltip} id={this.props.value} calloutProps={{ gapSpace: 0 }}>
        <CardBlock
          showTag={showTag}
          value={value}
          collapsed={collapsed}
          onClick={onClick}
          aria-describedby={this.props.value}
        >
          {labelPosition === 'top' ? <CardLabel {...labelProps} /> : null}
          <CardContainer>
            <CardNumber {...this.props} />
            {onActionIconClick && (
              <ActionIcon
                iconName={actionIconName || 'Info'}
                onClick={(e) => {
                  e.stopPropagation();
                  onActionIconClick();
                }}
                aria-label={actionIconName || 'Info'}
                title={actionIconHint || 'Info'}
              />
            )}
          </CardContainer>
          {labelPosition === 'bottom' ? <CardLabel {...labelProps} /> : null}
          {!!secondaryLabel ? (
            <CardLabel {...labelProps} label={secondaryLabel} fontStyle={secondaryLabelFontStyle} />
          ) : null}
        </CardBlock>
        {this.props.children}
      </TooltipHost>
    );
  }
}

ValueCard.propTypes = {
  collapsed: PropTypes.bool,
  includeLabel: PropTypes.bool,
  flexBox: PropTypes.bool,
  labelCols: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  inputCols: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  id: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  label: PropTypes.string,
  labelPosition: PropTypes.string,
  decimals: PropTypes.number, //Pass-through to <CardNumber/>
  onActionIconClick: () => {},
  actionIconName: PropTypes.string,
  actionIconHint: PropTypes.string,
};
ValueCard.defaultProps = {
  collapsed: false,
  onChange: () => {},
  includeLabel: true,
  inputPrefix: null,
  flexBox: true,
  labelCols: 1 / 4,
  inputCols: 3 / 4,
  value: '',
  label: '',
  labelPosition: 'bottom',
  id: '',
  tooltipFor: 'itemInformationTooltip',
  onActionIconClick: null,
  actionIconName: 'Info',
  actionIconHint: null,
};

export default ValueCard;
